import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Typography,
  Button,
  TextField,
  InputAdornment,
  LinearProgress,
  IconButton,
} from "@mui/material";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import UploadOutlinedIcon from "@mui/icons-material/UploadOutlined";
import { useDropzone } from "react-dropzone";
import LinkIcon from "@mui/icons-material/Link";
import { useSelector } from "react-redux";
import uploadIcon from "../../assets/upload.png";
import Thumbnail from "../../assets/Thumbnail.png";
import { FILE_SERVICE_BASE_URL } from "../../constant/apiUrl.js";
import { darkTheme, lightTheme } from "../../theme/Theme";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import AutorenewOutlinedIcon from "@mui/icons-material/AutorenewOutlined";
import useThemeRender from "../../theme/useThemeRender";
import axios from "../../API/API";
import { showErrorToast } from "../../utils/Toast/toast";
import {
  setDisableNextButton,
  setExternalFileDocument,
  setFileUploaded,
  setFileUploadPageName,
} from "../../slices/adminPageSlice.js";
import { useDispatch } from "react-redux";
import { setImage } from "./../../slices/imageSlice.js";
import {
  deleteCuratorFile,
  uploadYearlyReviewArtificatsFiles,
} from "../../API/curatorApi.js";

const ExternalStepOneForm = ({
  externalData,
  setExternalData,
  setFileType,
}) => {
  const [contentType, setContentType] = useState("DOCUMENT");
  // const [document, setDocument] = useState(null);
  const [isUploadingDocument, setUploadingDocument] = useState(false);
  // const [thumbnail, setThumbnail] = useState(null);
  const [isUploadingThumbnail, setUploadingThumbnail] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const thumbnailInputRef = useRef();
  const token = localStorage.getItem("auth_token");
  const dispatch = useDispatch();
  const { fileUploaded, externalFileDocument } = useSelector(
    (store) => store.adminpage
  );
  const { flagUser } = useSelector((store) => store.user);
  const thumbnail = useSelector((state) => state.images.images["thumbnail"]);
  const handlePreview = () => {
    if (externalFileDocument.file) {
      console.log(externalFileDocument.type);
      try {
        if (externalFileDocument.type === "application/pdf") {
          const fileURL = URL.createObjectURL(externalFileDocument.file);
          window.open(fileURL, "_blank");
        }
      } catch (error) {
        console.error("Error opening PDF:", error);
      }
    }
  };
  const isDarkTheme = useSelector((state) => state.theme);
  const handleLinkChange = (e) => {
    const newLink = e.target.value;
    setExternalData((prevData) => ({
      ...prevData,
      attachmentLinkUrl: newLink,
      attachmentFileId: 0,
    }));
    if (newLink) {
      dispatch(setDisableNextButton(false));
    } else {
      dispatch(setDisableNextButton(true));
    }
  };
  const getThemeColor = (lightColor, darkColor) =>
    isDarkTheme ? darkColor : lightColor;

  const handleChange = (event) => {
    setContentType(event.target.value);
    setExternalData((prevData) => ({
      ...prevData,
      contentTypeId: event.target.value,
    }));
  };

  const onDrop = async (acceptedFiles) => {
    const MAX_FILE_SIZE_MB = flagUser?.maxFileSize ?? 250;
    const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
    const file = acceptedFiles[0];
    if (!file) return;
    if (file.size > MAX_FILE_SIZE_BYTES) {
      showErrorToast(
        `File size exceeds the ${MAX_FILE_SIZE_MB} MB limit. Please upload a smaller file.`
      );
      return;
    }
    setUploadingDocument(true);

    try {
      // Step 1: Initiate upload
      const isVideo = file.type.startsWith("video/");
      setFileType(isVideo ? "VIDEO" : "DOCUMENT");
      const { data } = await uploadYearlyReviewArtificatsFiles(
        acceptedFiles,
        false,
        flagUser?.maxFileSize
      );
      const fileId = data.fileId;
      dispatch(
        setExternalFileDocument({
          name: file.name,
          type: file.type,
          file: file,
          fileId: fileId,
        })
      );
      setExternalData((prevData) => ({
        ...prevData,
        attachmentFileId: fileId,
        attachmentLinkUrl: "",
      }));
      dispatch(setDisableNextButton(false));
      dispatch(setFileUploaded(true));
      dispatch(setFileUploadPageName("internal"));
    } catch (error) {
      console.error("Error uploading file:", error);
    } finally {
      setUploadingDocument(false);
    }
  };
  useEffect(() => {
    if (contentType === "DOCUMENT") {
      if (
        !externalFileDocument ||
        Object?.keys(externalFileDocument)?.length === 0
      ) {
        dispatch(setFileUploaded(false));
        dispatch(setExternalFileDocument(null));
      } else {
        dispatch(setDisableNextButton(false));
        dispatch(setFileUploaded(true));
        dispatch(setFileUploadPageName("internal"));
      }
    } else if (contentType === "LINK") {
      if (
        externalData?.attachmentLinkUrl &&
        externalData?.attachmentLinkUrl?.trim() !== ""
      ) {
        dispatch(setDisableNextButton(false));
      } else {
        dispatch(setDisableNextButton(true));
      }
    }
  }, [contentType]);
  useEffect(() => {
    if (fileUploaded) {
      dispatch(setDisableNextButton(false));
    } else {
      dispatch(setDisableNextButton(true));
    }
  }, [fileUploaded]);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (files) => {
      void onDrop(files); // ✅ Sonar-safe
    },
    "application/pdf": [],
    "video/*": [],
  });

  const handleThumbnailUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadingThumbnail(true);

    // 16:9 validation is skipped for external users - they can upload any aspect ratio

    const formData = new FormData();
    formData.append("file", file);
    try {
      const response = await axios.post(
        `${FILE_SERVICE_BASE_URL}/fritolay/file-manager/thumbnail/upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      const data = response.data;
      // setThumbnail(URL.createObjectURL(file));
      const fileUrl = URL.createObjectURL(file);
      dispatch(setImage({ id: "thumbnail", url: fileUrl }));
      console.log("Upload successful:", data);
      setExternalData((prevData) => ({
        ...prevData,
        thumbnailFileId: data?.fileId,
      }));
    } catch (error) {
      // Handle error cases
      console.error(
        "Error uploading file:",
        error.response?.data || error.message
      );
    } finally {
      setUploadingThumbnail(false);
    }
  };

  const handleDocumentDelete = async (fileId) => {
    if (!fileId) {
      console.error("File ID is required to delete the file.");
      return;
    }

    try {
      await deleteCuratorFile(fileId);

      dispatch(setFileUploaded(false));
      dispatch(setExternalFileDocument(null));
    } catch (error) {
      console.error("Error deleting the file:", error);
    }
  };

  const renderProgressBar = () => (
    <Box sx={{ width: "100%", mt: 2 }}>
      <LinearProgress />
    </Box>
  );
  const insideCardBackgroundColorLayer3 = useThemeRender(
    "insideCardBackgroundColorLayer3"
  );

  const renderDropzone = (label) => {
    const handleDragOver = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(true);
    };

    const handleDragEnter = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(true);
    };

    const handleDragLeave = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
    };

    const handleDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const file = e.dataTransfer.files[0];
      if (file) {
        handleThumbnailUpload({ target: { files: [file] } }); // Simulate input file event
      }
    };

    return (
      <Box>
        <FormLabel
          sx={{
            color: getThemeColor(
              lightTheme.textColorLayer1,
              darkTheme.textColorLayer1
            ),
          }}
        >
          {label}
        </FormLabel>
        <Box
          sx={{
            display: "flex",
            mt: 2,
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            borderRadius: "8px",
            border: isDragging ? "2px solid #005CBC" : "1px dashed #D7E5F8",
            background: isDragging
              ? "#E3F2FD"
              : getThemeColor(
                  lightTheme.insideCardBackgroundColorLayer3,
                  darkTheme.insideCardBackgroundColorLayer3
                ),
            padding: "24px",
            width: { xs: "100%", sm: "50%" },
            position: "relative",
            transition: "background 0.2s ease, border 0.2s ease",
          }}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {isUploadingThumbnail ? (
            renderProgressBar()
          ) : thumbnail ? (
            <>
              <img
                src={thumbnail}
                alt="Uploaded Thumbnail"
                style={{
                  maxWidth: "100%",
                  borderRadius: "4px",
                  objectFit: "cover",
                }}
              />
              <Button
                variant="contained"
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "45%",
                  borderRadius: "24px",
                  fontWeight: 700,
                }}
                startIcon={<AutorenewOutlinedIcon />}
                onClick={() => thumbnailInputRef.current.click()}
              >
                Replace
              </Button>
              <input
                type="file"
                accept="image/*"
                ref={thumbnailInputRef}
                style={{ display: "none" }}
                onChange={handleThumbnailUpload}
              />
            </>
          ) : (
            <>
              <img src={Thumbnail} alt="Thumbnail Icon" />
              <Typography
                variant="body1"
                sx={{
                  textAlign: "center",
                  color: getThemeColor(
                    lightTheme.textColorLayer1,
                    darkTheme.textColorLayer1
                  ),
                }}
              >
                {isDragging
                  ? "Drop the file here"
                  : "Drag and drop your file here (only .jpg, .png)"}
              </Typography>
              <Button
                variant="text"
                sx={{
                  mt: 2,
                  borderRadius: "24px",
                  border: "2px solid #005CBC",
                  fontWeight: 700,
                  py: 1,
                  px: 2,
                }}
                startIcon={<UploadOutlinedIcon />}
                onClick={() => thumbnailInputRef.current.click()}
              >
                Upload File
              </Button>
              <input
                type="file"
                accept="image/*"
                ref={thumbnailInputRef}
                style={{ display: "none" }}
                onChange={handleThumbnailUpload}
              />
            </>
          )}
        </Box>
      </Box>
    );
  };

  const dropzoneStyle = {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "8px",
    border: "1px dashed #D7E5F8",
    padding: "24px",
    background: useThemeRender("insideCardBackgroundColorLayer3"),
    width: { xs: "100%", sm: "50%" },
  };
  return (
    <Box
      sx={{
        mt: 2,
        mb: 2,
        display: "flex",
        flexDirection: "column",
        padding: "16px",
        background: getThemeColor(
          lightTheme.cardBackgroundColorLayer1,
          darkTheme.cardBackgroundColorLayer1
        ),
      }}
    >
      <Typography
        sx={{
          color: useThemeRender("textColorLayer1"),
          fontFeatureSettings: "'liga' off, 'clig' off",
          fontFamily: "Inter",
          fontSize: "24px",
          fontStyle: "normal",
          fontWeight: 700,
          lineHeight: "32px",
          mb: 4,
        }}
      >
        Step 1: Upload
      </Typography>

      <FormLabel
        sx={{
          color: getThemeColor(
            lightTheme.textColorLayer1,
            darkTheme.textColorLayer1
          ),
        }}
      >
        Content type
      </FormLabel>
      <RadioGroup value={contentType} onChange={handleChange} row>
        {/* {["DOCUMENT", "LINK"].map((type) => (
          <FormControlLabel
            key={type}
            value={type}
            control={
              <Radio
                sx={{
                  color: getThemeColor(
                    lightTheme.radioButtonLayer1,
                    darkTheme.radioButtonLayer1
                  ),
                }}
              />
            }
            label={type.charAt(0).toUpperCase() + type.slice(1)}
          />
        ))} */}
        <FormControlLabel
          value="DOCUMENT"
          control={
            <Radio
              sx={{
                color: getThemeColor(
                  lightTheme.radioButtonLayer1,
                  darkTheme.radioButtonLayer1
                ),
              }}
            />
          }
          label="Upload"
        />
        <FormControlLabel
          value="LINK"
          control={
            <Radio
              sx={{
                color: getThemeColor(
                  lightTheme.radioButtonLayer1,
                  darkTheme.radioButtonLayer1
                ),
              }}
            />
          }
          label="Link"
        />
      </RadioGroup>

      {contentType === "LINK" && (
        <TextField
          placeholder="Paste Link"
          sx={{ mt: 2, width: "100%" }}
          value={externalData.attachmentLinkUrl}
          onChange={handleLinkChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LinkIcon />
              </InputAdornment>
            ),
          }}
        />
      )}

      {contentType === "DOCUMENT" && (
        <>
          <Box>
            {!externalFileDocument ? (
              <>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  sx={{ width: { xs: "100%", sm: "50%" } }}
                >
                  <FormLabel
                    sx={{
                      color: getThemeColor(
                        lightTheme.textColorLayer1,
                        darkTheme.textColorLayer1
                      ),
                    }}
                  >
                    Document
                  </FormLabel>
                  <Box display="flex" justifyContent="space-between">
                    <FormLabel
                      sx={{
                        color: getThemeColor(
                          lightTheme.textColorLayer4,
                          darkTheme.textColorLayer4
                        ),
                        fontSize: "14px",
                      }}
                    >
                      *Required
                    </FormLabel>
                  </Box>
                </Box>
                <Box
                  {...getRootProps()}
                  sx={{ mt: 2, mb: 2, ...dropzoneStyle }}
                >
                  {isUploadingDocument ? (
                    renderProgressBar()
                  ) : (
                    <img src={uploadIcon} alt="Upload Icon" />
                  )}
                  <input {...getInputProps()} />
                  <Typography
                    sx={{
                      color: getThemeColor(
                        lightTheme.textColorLayer1,
                        darkTheme.textColorLayer1
                      ),
                    }}
                  >
                    Drag and drop your file here
                  </Typography>
                  <Button
                    variant="text"
                    sx={{
                      mt: 2,
                      borderRadius: "24px",
                      border: "2px solid #005CBC",
                      fontWeight: 700,
                      py: 1,
                      px: 2,
                    }}
                    startIcon={<UploadOutlinedIcon />}
                  >
                    Upload File
                  </Button>
                </Box>
              </>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  background: insideCardBackgroundColorLayer3,
                  width: { xs: "100%", sm: "50%" },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                  }}
                  onClick={handlePreview}
                >
                  <InsertDriveFileOutlinedIcon
                    sx={{ marginRight: 1, color: "primary.main" }}
                  />
                  <Typography> {externalFileDocument?.name}</Typography>
                </Box>
                <IconButton
                  onClick={() =>
                    handleDocumentDelete(externalFileDocument?.fileId)
                  }
                >
                  <DeleteOutlineOutlinedIcon color="primary" />
                </IconButton>
              </Box>
            )}
          </Box>
          {renderDropzone("Thumbnail upload")}
        </>
      )}

      {contentType === "LINK" && renderDropzone("Thumbnail upload")}
    </Box>
  );
};

export default ExternalStepOneForm;
